import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Slider,
  Divider,
} from '@mui/material';
import { Timeline, TrendingUp, TrendingDown, ShowChart } from '@mui/icons-material';
import { apiService, PatternMatchResult } from '../services/api';
import { useTheme } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const PatternMatching: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [windowDays, setWindowDays] = useState(5);
  const [method, setMethod] = useState('all');
  const [topN, setTopN] = useState(10);
  const [historyYears, setHistoryYears] = useState(10);
  const [predictionDays, setPredictionDays] = useState(21);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PatternMatchResult | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<number>(0);

  // Timeframe presets
  const timeframePresets = [
    { label: '1 Day', days: 1 },
    { label: '3 Days', days: 3 },
    { label: '1 Week', days: 5 },
    { label: '2 Weeks', days: 10 },
    { label: '1 Month', days: 21 },
    { label: '3 Months', days: 63 },
    { label: '6 Months', days: 126 },
    { label: '1 Year', days: 252 },
  ];

  const handleFindPatterns = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await apiService.findSimilarPatterns(
        symbol.toUpperCase(),
        windowDays,
        method,
        topN,
        historyYears,
        predictionDays
      );
      setResult(data);
      setSelectedMatch(0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to find patterns');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getPriceChangeColor = (change: number) => {
    if (change > 0) return theme.palette.success.main;
    if (change < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  // Calculate time distance from current pattern start to match start date
  const getTimeDistance = (matchStartDate: string): string => {
    if (!result) return '';

    const currentStartDate = new Date(result.current_start_date);
    const matchStart = new Date(matchStartDate);

    // Calculate difference in milliseconds
    const diffMs = currentStartDate.getTime() - matchStart.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    // Convert to years and months
    const years = Math.floor(diffDays / 365.25);
    const remainingDays = diffDays - (years * 365.25);
    const months = Math.floor(remainingDays / 30.44); // Average days per month

    return `${years}.${months}`;
  };

  // Prepare chart data for selected match
  const getChartData = () => {
    if (!result || !result.matches[selectedMatch]) return [];

    const currentPattern = result.current_pattern;
    const historicalPattern = result.matches[selectedMatch].pattern_data;
    const futurePattern = result.matches[selectedMatch].future_pattern;

    const maxLength = Math.max(
      currentPattern.length,
      historicalPattern.length + (futurePattern?.length || 0)
    );
    const data = [];

    for (let i = 0; i < maxLength; i++) {
      const dataPoint: any = {
        index: i,
        current: currentPattern[i] !== undefined ? currentPattern[i] : null,
        historical: historicalPattern[i] !== undefined ? historicalPattern[i] : null,
      };

      // Add future data if available (starts from last historical point to create continuation)
      if (futurePattern) {
        if (i === historicalPattern.length - 1) {
          // At the connection point, set future to historical value to connect lines
          dataPoint.future = historicalPattern[i];
        } else if (i > historicalPattern.length - 1) {
          // After the connection point, use future pattern data
          const futureIndex = i - historicalPattern.length;
          if (futureIndex < futurePattern.length) {
            dataPoint.future = futurePattern[futureIndex];
          }
        }
      }

      data.push(dataPoint);
    }

    return data;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Timeline />
        <Typography variant="h4" fontWeight={700}>
          Pattern Matching
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Find similar historical price patterns using 5 state-of-the-art algorithms: Matrix Profile, DTW, SAX, PLA, and Cross-Correlation
      </Typography>

      {/* Input Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="AAPL"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Pattern Window</InputLabel>
                <Select
                  value={windowDays}
                  label="Pattern Window"
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                >
                  {timeframePresets.map((preset) => (
                    <MenuItem key={preset.days} value={preset.days}>
                      {preset.label} ({preset.days} days)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Algorithm</InputLabel>
                <Select
                  value={method}
                  label="Algorithm"
                  onChange={(e) => setMethod(e.target.value)}
                >
                  <MenuItem value="all">All (Weighted Combo)</MenuItem>
                  <MenuItem value="matrix_profile">Matrix Profile</MenuItem>
                  <MenuItem value="dtw">DTW (Time Warping)</MenuItem>
                  <MenuItem value="sax">SAX (Symbolic)</MenuItem>
                  <MenuItem value="pla">PLA (Slopes)</MenuItem>
                  <MenuItem value="cross_correlation">Cross-Correlation</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Top Matches</InputLabel>
                <Select
                  value={topN}
                  label="Top Matches"
                  onChange={(e) => setTopN(Number(e.target.value))}
                >
                  <MenuItem value={5}>Top 5</MenuItem>
                  <MenuItem value={10}>Top 10</MenuItem>
                  <MenuItem value={20}>Top 20</MenuItem>
                  <MenuItem value={50}>Top 50</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 3 } }}>
                <Typography variant="caption" gutterBottom display="block">
                  Historical Data Range: {historyYears} years
                </Typography>
                <Slider
                  value={historyYears}
                  onChange={(_, value) => setHistoryYears(value as number)}
                  min={1}
                  max={40}
                  marks={[
                    { value: 1, label: '1Y' },
                    { value: 10, label: '10Y' },
                    { value: 20, label: '20Y' },
                    { value: 30, label: '30Y' },
                    { value: 40, label: '40Y' },
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ px: 1 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 3 }, pr: 3 }}>
                <Typography variant="caption" gutterBottom display="block">
                  Prediction Time Window: {predictionDays} days
                </Typography>
                <Slider
                  value={predictionDays}
                  onChange={(_, value) => setPredictionDays(value as number)}
                  min={5}
                  max={120}
                  marks={[
                    { value: 5, label: '5D' },
                    { value: 21, label: '1M' },
                    { value: 60, label: '2M' },
                    { value: 90, label: '3M' },
                    { value: 120, label: '4M' },
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ px: 1 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleFindPatterns}
                disabled={loading || !symbol}
                fullWidth
              >
                {loading ? 'Finding Patterns...' : 'Find Similar Patterns'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Analyzing {historyYears} years of historical data...
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Results */}
      {result && !loading && (
        <>
          {/* Statistics Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {result.symbol} - Pattern Analysis Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Pattern Window
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {result.window_days} days
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {result.current_start_date} to {result.current_end_date}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Matches Found
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {result.statistics.total_matches}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Score: {result.statistics.avg_match_score.toFixed(1)}
                    </Typography>
                  </Paper>
                </Grid>

                {!result.statistics.no_future_data && result.statistics.avg_future_change !== undefined && (
                  <>
                    <Grid item xs={6} md={3}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: getPriceChangeColor(result.statistics.avg_future_change) + '20',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Avg Future Change
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {result.statistics.avg_future_change > 0 ? (
                            <TrendingUp sx={{ color: getPriceChangeColor(result.statistics.avg_future_change) }} />
                          ) : (
                            <TrendingDown sx={{ color: getPriceChangeColor(result.statistics.avg_future_change) }} />
                          )}
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: getPriceChangeColor(result.statistics.avg_future_change) }}
                          >
                            {result.statistics.avg_future_change > 0 ? '+' : ''}
                            {result.statistics.avg_future_change.toFixed(2)}%
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="caption" color="text.secondary">
                          Historical Outcomes
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip
                            label={`↑ ${result.statistics.bullish_outcomes}`}
                            color="success"
                            size="small"
                          />
                          <Chip
                            label={`↓ ${result.statistics.bearish_outcomes}`}
                            color="error"
                            size="small"
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>

              {!result.statistics.no_future_data && result.statistics.avg_future_change !== undefined && (
                <Alert
                  severity={result.statistics.avg_future_change > 2 ? 'success' : result.statistics.avg_future_change < -2 ? 'error' : 'info'}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Pattern Prediction:</strong> Based on {result.statistics.total_matches} historical matches,
                    the average outcome was {result.statistics.avg_future_change > 0 ? 'a gain' : 'a loss'} of{' '}
                    <strong>{Math.abs(result.statistics.avg_future_change).toFixed(2)}%</strong>.
                    {result.statistics.bullish_outcomes !== undefined && result.statistics.bearish_outcomes !== undefined && (
                      <> {result.statistics.bullish_outcomes} patterns led to gains, {result.statistics.bearish_outcomes} to losses.</>
                    )}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Pattern Comparison Chart */}
          {result.matches.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    <ShowChart sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Pattern Comparison - Match #{selectedMatch + 1}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Select Match</InputLabel>
                    <Select
                      value={selectedMatch}
                      label="Select Match"
                      onChange={(e) => setSelectedMatch(Number(e.target.value))}
                    >
                      {result.matches.map((match, idx) => (
                        <MenuItem key={idx} value={idx}>
                          Match #{idx + 1} - {match.start_date} (Score: {match.match_score.toFixed(1)}, {getTimeDistance(match.start_date)}y ago)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="index"
                      label={{ value: 'Days', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis
                      label={{ value: 'Price Change (%)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      formatter={(value: any) => `${Number(value).toFixed(2)}%`}
                      labelFormatter={(label) => `Day ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="current"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      name="Current Pattern"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="historical"
                      stroke={theme.palette.secondary.main}
                      strokeWidth={2}
                      name={`Historical Pattern (${result.matches[selectedMatch].start_date})`}
                      dot={false}
                    />
                    {result.matches[selectedMatch].future_pattern && (
                      <Line
                        type="monotone"
                        dataKey="future"
                        stroke={theme.palette.success.main}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name={`What Happened Next (${predictionDays}d)`}
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Match Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date Range: {result.matches[selectedMatch].start_date} to {result.matches[selectedMatch].end_date}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start Price: ${result.matches[selectedMatch].start_price.toFixed(2)} | End Price: ${result.matches[selectedMatch].end_price.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Similarity Scores (0-100, higher is better)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Combined: ${result.matches[selectedMatch].match_score.toFixed(1)}`}
                        sx={{ bgcolor: getScoreColor(result.matches[selectedMatch].match_score), color: 'white' }}
                      />
                      <Chip
                        label={`Matrix Profile: ${result.matches[selectedMatch].matrix_profile_score.toFixed(1)}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`DTW: ${result.matches[selectedMatch].dtw_score.toFixed(1)}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                      <Chip
                        label={`SAX: ${result.matches[selectedMatch].sax_score.toFixed(1)}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`PLA: ${result.matches[selectedMatch].pla_score.toFixed(1)}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`X-Corr: ${result.matches[selectedMatch].cross_correlation_score.toFixed(1)}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                </Grid>

                {result.matches[selectedMatch].future_price_change && (
                  <Alert
                    severity={
                      result.matches[selectedMatch].future_price_change!.final_change > 2
                        ? 'success'
                        : result.matches[selectedMatch].future_price_change!.final_change < -2
                        ? 'error'
                        : 'info'
                    }
                    sx={{ mt: 2 }}
                  >
                    <Typography variant="body2">
                      <strong>What Happened Next:</strong> After this historical pattern,
                      the price changed by{' '}
                      <strong>
                        {result.matches[selectedMatch].future_price_change!.final_change > 0 ? '+' : ''}
                        {result.matches[selectedMatch].future_price_change!.final_change.toFixed(2)}%
                      </strong>{' '}
                      over the next {result.matches[selectedMatch].future_price_change!.days_forward} days
                      (Range: {result.matches[selectedMatch].future_price_change!.min_change.toFixed(2)}% to{' '}
                      {result.matches[selectedMatch].future_price_change!.max_change.toFixed(2)}%)
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Matches Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                All Pattern Matches
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Date Range</TableCell>
                      <TableCell align="right">Combined</TableCell>
                      <TableCell align="right">Matrix</TableCell>
                      <TableCell align="right">DTW</TableCell>
                      <TableCell align="right">SAX</TableCell>
                      <TableCell align="right">PLA</TableCell>
                      <TableCell align="right">X-Corr</TableCell>
                      <TableCell align="right">Start $</TableCell>
                      <TableCell align="right">End $</TableCell>
                      <TableCell align="right">Future Δ</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.matches.map((match, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          bgcolor: selectedMatch === idx ? theme.palette.action.selected : 'inherit',
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedMatch(idx)}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {match.start_date}
                            <br />
                            to {match.end_date}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={match.match_score.toFixed(1)}
                            size="small"
                            sx={{ bgcolor: getScoreColor(match.match_score), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell align="right">{match.matrix_profile_score.toFixed(1)}</TableCell>
                        <TableCell align="right">{match.dtw_score.toFixed(1)}</TableCell>
                        <TableCell align="right">{match.sax_score.toFixed(1)}</TableCell>
                        <TableCell align="right">{match.pla_score.toFixed(1)}</TableCell>
                        <TableCell align="right">{match.cross_correlation_score.toFixed(1)}</TableCell>
                        <TableCell align="right">${match.start_price.toFixed(2)}</TableCell>
                        <TableCell align="right">${match.end_price.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {match.future_price_change ? (
                            <Typography
                              variant="body2"
                              sx={{ color: getPriceChangeColor(match.future_price_change.final_change) }}
                            >
                              {match.future_price_change.final_change > 0 ? '+' : ''}
                              {match.future_price_change.final_change.toFixed(2)}%
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setSelectedMatch(idx)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Algorithm Info */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                About the 5 State-of-the-Art Pattern Matching Algorithms
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                This tool uses 5 cutting-edge algorithms specifically selected for stock price pattern matching.
                Each algorithm has unique strengths for detecting different types of patterns:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Matrix Profile (30% weight)" size="small" sx={{ mr: 1 }} color="primary" />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <strong>State-of-the-art subsequence similarity detection.</strong> Uses STOMP algorithm to find
                      the most similar patterns even with noise and scaling differences. Best overall for stock patterns.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="DTW - Dynamic Time Warping (15%)" size="small" sx={{ mr: 1 }} color="secondary" />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <strong>Handles time shifts and volatility.</strong> Excellent for patterns that occurred at
                      different speeds or with slight timing variations. Allows flexible alignment.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="SAX - Symbolic Approximation (20%)" size="small" sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <strong>Direction-based symbolic matching.</strong> Converts patterns into directional symbols
                      (up/down/flat). Robust to noise, excellent for trend-based pattern recognition.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="PLA - Piecewise Linear (25%)" size="small" sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <strong>Slope-based for technical patterns.</strong> Breaks patterns into line segments and
                      compares slopes. Ideal for detecting chart patterns like head & shoulders, wedges, triangles.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip label="Cross-Correlation (10%)" size="small" sx={{ mr: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      <strong>Lag-tolerant similarity with FFT.</strong> Fast correlation-based matching that handles
                      time lags. Good for detecting shifted versions of the same pattern.
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight={600}>
                      Combined "All" Method:
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Weighted average: Matrix Profile (30%) and PLA (25%) carry the most weight — PLA
                      compares the slope structure of the two windows, which is what a "similar pattern"
                      means to a reader. SAX (20%) adds directional robustness, DTW (15%) handles timing
                      shifts, and Cross-Correlation (10%) provides fast lag detection.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PatternMatching;
